import React, { createContext, useState, useContext, useEffect } from 'react';

import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUser = React.useCallback(async () => {
    try {
      const response = await authService.getMe();
      setUser(response.data.data);
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('token');
      authService.setToken(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.setToken(token);
      loadUser();
    } else {
      setLoading(false);
    }
  }, [loadUser]);


  const login = React.useCallback(async (credentials) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      const { token, ...userData } = response.data.data;
      localStorage.setItem('token', token);
      authService.setToken(token);
      setUser(userData);
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = React.useCallback(async (userData) => {
    try {
      const response = await authService.register(userData);
      const token = response.data?.data?.token;
      if (token) {
        localStorage.setItem('token', token);
        authService.setToken(token);
        setUser(response.data.data);
      }
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      throw error;
    }
  }, []);

  const logout = React.useCallback(() => {
    localStorage.removeItem('token');
    authService.setToken(null);
    setUser(null);
  }, []);

  const updateUser = React.useCallback((userData) => {
    setUser(userData);
  }, []);

  const updateProfile = React.useCallback(async (userData) => {
    try {
      const response = await authService.updateProfile(userData);
      setUser(response.data.data);
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Profile update failed');
      throw error;
    }
  }, []);

  const clearError = React.useCallback(() => setError(null), []);

  const value = React.useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    updateProfile,
    clearError,
  }), [user, loading, error, login, register, logout, updateUser, updateProfile, clearError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
